import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  CacheVersionsMap,
  CacheVersionsResponse
} from '../../interfaces/cache-versions/cache-versions.interface';
import { SKIP_STORE_API_KEY } from '../../interceptors/http-context-tokens';

@Injectable({ providedIn: 'root' })
export class CacheVersionsApiService {
  private readonly httpClient: HttpClient = inject(HttpClient);

  /**
   * Fetches the latest cache version map for the current tenant. Returns
   * `null` on any failure so the reconciler can treat the result as a no-op
   * without breaking the app.
   *
   * The `tenant` query param is added by the existing
   * `happyApiHeadersInterceptor`; the `SKIP_STORE_API_KEY` context flag
   * prevents the `store_api_key` query param from being attached because this
   * endpoint is tenant-scoped, not store-scoped.
   *
   * NOTE: We bypass `BaseApiService.get` here on purpose. That helper runs
   * `mergeDeep` on the request options, which would convert the
   * `HttpContext` class instance into a plain object and break
   * `req.context.get(...)` inside the interceptor.
   */
  getCacheVersions(): Observable<CacheVersionsMap | null> {
    const context: HttpContext = new HttpContext().set(
      SKIP_STORE_API_KEY,
      true
    );
    const url = `${environment.happyApi.baseUrl}/cache/versions`;

    return this.httpClient
      .get<CacheVersionsResponse>(url, { context })
      .pipe(
        map((response) => {
          const versions = response?.data?.versions;
          return versions && typeof versions === 'object' ? versions : null;
        }),
        catchError(() => of<CacheVersionsMap | null>(null))
      );
  }
}
